import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Gift, Users, Copy, Mail, CheckCircle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function Referrals() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [emails, setEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };

  const copyReferralLink = () => {
    if (stats?.referral_code) {
      const link = `https://chronos.app?ref=${stats.referral_code}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(t('referral.linkCopied'));
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const sendInvites = async () => {
    const emailList = emails.split(',').map(e => e.trim()).filter(e => e);
    
    if (emailList.length === 0) {
      toast.error(t('referral.enterEmails'));
      return;
    }

    if (emailList.length > 10) {
      toast.error(t('referral.maxEmails'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/referral/send-invites`,
        emailList,
        { withCredentials: true }
      );
      
      toast.success(t('referral.invitesSent', { count: response.data.sent_count }));
      setShowInviteDialog(false);
      setEmails('');
    } catch (error) {
      console.error('Error sending invites:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (!stats) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <Gift className="h-8 w-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-gray-900">{t('referral.title')}</h1>
        </div>

        {/* Referral Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 border-l-4 border-teal-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('referral.totalReferrals')}</p>
                <p className="text-3xl font-bold text-teal-600 mt-2">{stats.total_referrals}</p>
              </div>
              <Users className="h-10 w-10 text-teal-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('referral.creditsEarned')}</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.credits_earned}</p>
              </div>
              <Gift className="h-10 w-10 text-yellow-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('referral.potentialEarnings')}</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">∞</p>
              </div>
              <Gift className="h-10 w-10 text-purple-500 opacity-50" />
            </div>
          </Card>
        </div>

        {/* Referral Code Card */}
        <Card className="p-8 bg-gradient-to-br from-teal-50 via-cyan-50 to-teal-50 border-2 border-teal-200">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('referral.yourCode')}</h2>
            <p className="text-gray-600 mb-6">{t('referral.shareCode')}</p>
            
            <div className="bg-white rounded-lg p-6 inline-block border-2 border-teal-500 shadow-lg mb-6">
              <p className="text-5xl font-bold text-teal-600 tracking-wider">{stats.referral_code}</p>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button
                onClick={copyReferralLink}
                className="bg-teal-500 hover:bg-teal-600 text-white gap-2"
              >
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('referral.copied') : t('referral.copyLink')}
              </Button>
              
              <Button
                onClick={() => setShowInviteDialog(true)}
                variant="outline"
                className="border-teal-500 text-teal-600 hover:bg-teal-50 gap-2"
              >
                <Mail className="h-4 w-4" />
                {t('referral.inviteFriends')}
              </Button>
            </div>
          </div>
        </Card>

        {/* How it Works */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-4">{t('referral.howItWorks')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-teal-600 font-bold text-xl">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">{t('referral.step1Title')}</h4>
                <p className="text-gray-600 text-sm">{t('referral.step1Desc')}</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-cyan-600 font-bold text-xl">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">{t('referral.step2Title')}</h4>
                <p className="text-gray-600 text-sm">{t('referral.step2Desc')}</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold text-xl">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">{t('referral.step3Title')}</h4>
                <p className="text-gray-600 text-sm">{t('referral.step3Desc')}</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 font-bold text-xl">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">{t('referral.step4Title')}</h4>
                <p className="text-gray-600 text-sm">{t('referral.step4Desc')}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Referred Users */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-6">{t('referral.yourReferrals')}</h3>
          
          {stats.referred_users.length === 0 ? (
            <p className="text-center text-gray-500 py-8">{t('referral.noReferralsYet')}</p>
          ) : (
            <div className="space-y-3">
              {stats.referred_users.map((refUser) => (
                <div key={refUser.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={refUser.picture} />
                    <AvatarFallback className="bg-teal-100 text-teal-600">
                      {refUser.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{refUser.name}</p>
                    <p className="text-sm text-gray-500">@{refUser.username}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{t('referral.joined')}</p>
                    <p className="text-sm font-medium text-teal-600">
                      {new Date(refUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('referral.inviteFriends')}</DialogTitle>
            <DialogDescription>
              {t('referral.inviteDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('referral.emailAddresses')}</label>
              <Input
                placeholder={t('referral.emailPlaceholder')}
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">{t('referral.separateComma')}</p>
            </div>
            
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-800">
                💡 {t('referral.bonusInfo')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={sendInvites}
              disabled={loading}
              className="bg-teal-500 hover:bg-teal-600"
            >
              {loading ? t('common.processing') : t('referral.sendInvites')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
